import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Box } from "@flexisaf/flexibull2/build/layout";
import { Text } from "@flexisaf/flexibull2/build/typo";

import { HookForm as Form, Grid, Stepper } from "@/components";
import { useStepper } from "@/components/stepper/Stepper";
import { useGetProductsQuery, useLazyGetInstitutionsQuery } from "@/api/productIntegration";
import { useGetCustomersQuery } from "@/api/customer";
import { getSchoolAsOptions } from "@/utils/help";
import { ContentBaseWrapper } from "./productIntegrationStyles";
import { customerAndProductDetailsValidation } from "../productIntegrationValidation";
import { useProductIntegration } from "../productIntegrationUtils";
import { theme } from "@/style";

const { CardSelector } = Form;

export default function CustomerAndProductDetails() {
    const { data: products, isLoading: isLoadingProducts } = useGetProductsQuery();
    const { addToValues, warnOnUnload, toggleWarnOnUnload, values, isEditMode } =
        useProductIntegration();
    const { handleNext } = useStepper();

    const form = useForm({
        resolver: yupResolver(customerAndProductDetailsValidation),
        defaultValues: {
            description: values.description || "",
            institutionId: values.institutionId || "",
            schoolId: values.schoolId || "",
            productId: values.productId || "",
        },
    });

    const productId = form.watch("productId");
    const isProductSelected = !!productId;

    const [fetchInstitutions] = useLazyGetInstitutionsQuery();
    const selectedInstitutionRef = useRef(null);
    const { data: customers, isLoading: isLoadingCustomers } = useGetCustomersQuery(null);

    const loadInstitutionOptions = useCallback(
        (inputValue, callback) => {
            if (!productId) {
                callback([]);
                return;
            }
            const searchTerm = inputValue ?? "";
            fetchInstitutions({
                productName: productId,
                size: 1000,
                search: searchTerm,
            })
                .unwrap()
                .then((institutions) => {
                    const options = (institutions || []).map((inst) => {
                        const base = getSchoolAsOptions([inst], { appendIdToLabel: true })[0];
                        return { ...base, name: inst.name };
                    });
                    callback(options);
                })
                .catch(() => callback([]));
        },
        [productId, fetchInstitutions],
    );

    useEffect(() => {
        if (!isEditMode) return;
        if (!values.productId && !values.schoolId && !values.institutionId && !values.description)
            return;
        if (form.formState.isDirty) return;

        form.reset({
            description: values.description || "",
            institutionId: values.institutionId || "",
            schoolId: values.schoolId || "",
            productId: values.productId || "",
        });
    }, [
        form,
        isEditMode,
        values.description,
        values.institutionId,
        values.productId,
        values.schoolId,
        form.formState.isDirty,
    ]);

    useEffect(() => {
        if (form.formState.isDirty && !warnOnUnload) {
            toggleWarnOnUnload();
        }
    }, [form.formState.isDirty, warnOnUnload, toggleWarnOnUnload]);

    // Reset institution when product changes
    useEffect(() => {
        if (productId !== values.productId) {
            form.setValue("institutionId", "");
            form.setValue("description", "");
            selectedInstitutionRef.current = null;
        }
    }, [form, productId, values.productId]);

    const onSubmit = (formBody) => {
        const schoolName = selectedInstitutionRef.current?.name ?? "";
        const selectedCustomer = customers?.find((customer) => customer.id === formBody.schoolId);
        const customerName = selectedCustomer?.name || "";

        addToValues({
            ...formBody,
            schoolName,
            customerName,
        });
        handleNext();
    };

    const productIdError = form.formState.errors?.productId?.message;

    return (
        <ContentBaseWrapper>
            <div className="content">
                <Box margin="2.5em 0 0" />
                <Form form={form}>
                    <Grid style={{ maxWidth: "30em" }} gap="3.8em">
                        <Box>
                            <Text
                                size="0.72rem"
                                style={{
                                    marginBottom: "1rem",
                                    color: "#383A3F",
                                    fontWeight: "600",
                                }}
                            >
                                Select the Product you wish to integrate
                            </Text>
                            <Grid default="1fr / 1fr" desktop="1fr / 1fr 1fr 1fr">
                                {isLoadingProducts ? (
                                    <Text color={theme.PrimaryGrey}>Loading products...</Text>
                                ) : (
                                    products?.map((product) => (
                                        <CardSelector
                                            name="productId"
                                            key={product.value}
                                            label={product.label}
                                            value={product.value}
                                            view="label"
                                        />
                                    ))
                                )}
                            </Grid>
                            {productIdError && (
                                <Text color={theme.PrimaryRed} style={{ marginTop: "0.5em" }}>
                                    {productIdError}
                                </Text>
                            )}
                        </Box>

                        <Form.AsyncSelect
                            name="institutionId"
                            label="Name of Institution/Company"
                            loadOptions={loadInstitutionOptions}
                            disable={!isProductSelected}
                            defaultOptionsDependency={productId}
                            onOptionSelect={(option) => {
                                selectedInstitutionRef.current = option;
                                form.setValue(
                                    "description",
                                    option ? (option.name ?? option.label ?? "") : "",
                                );
                            }}
                        />
                        <Form.Select
                            name="schoolId"
                            label="Customer"
                            options={getSchoolAsOptions(customers, { appendIdToLabel: true })}
                            disable={isLoadingCustomers || !customers}
                            isLoading={isLoadingCustomers}
                        />
                    </Grid>
                </Form>
            </div>
            <Stepper.StepButtons onNext={form.handleSubmit(onSubmit)} />
        </ContentBaseWrapper>
    );
}
